import { useState, useCallback, useMemo } from 'react';
import { ChangeRequest, ChangeRequestCreateRequest } from '@/api/bol/models';
import { getLogisticsObjectIdKey, type LogisticsObjectEntity } from '@/types/logisticsObject';
import { getLogisticsObjectsApi } from '@/services/bolApiClient';
interface UseChangeRequestActionsProps {
  logisticsObjectType: LogisticsObjectEntity;
  objectId: string;
  onSuccess?: () => void | Promise<void>;
}

export const useChangeRequestActions = ({
  logisticsObjectType,
  objectId,
  onSuccess
}: UseChangeRequestActionsProps) => {
  const api = useMemo(
    () => getLogisticsObjectsApi(logisticsObjectType),
    [logisticsObjectType],
  );
  const idKey = useMemo(
    () => getLogisticsObjectIdKey(logisticsObjectType),
    [logisticsObjectType],
  );
  const buildIdParams = useCallback(
    () => ({ [idKey]: objectId } as Record<string, string>),
    [idKey, objectId],
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [patching, setPatching] = useState(false);
  const [patchError, setPatchError] = useState<string | null>(null);

  const createChangeRequest = useCallback(async (request: ChangeRequestCreateRequest) => {
    if (!request.operations.length) {
      setCreateError('At least one operation is required');
      return false;
    }

    if (!request.requestorMemberIdentifier) {
      setCreateError('Requestor member identifier is required');
      return false;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await api.createChangeRequestOnLogisticsObjectById({
        ...buildIdParams(),
        contentType: 'application/json' as const,
        changeRequestCreateRequest: request,
        source: true,
      });

      await onSuccess?.();
      return true;
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create change request');
      return false;
    } finally {
      setCreating(false);
    }
  }, [api, buildIdParams, onSuccess]);

  const patchChangeRequest = useCallback(async (
    changeRequest: ChangeRequest,
    status: 'ACCEPTED' | 'REJECTED'
  ) => {
    setPatching(true);
    setPatchError(null);

    try {
      const patchRequest = { status };
      await api.updateChangeRequestOnLogisticsObjectById({
        ...buildIdParams(),
        changeRequestId: changeRequest.id || '',
        contentType: 'application/json' as const,
        changeRequestPatchRequest: patchRequest,
      });

      await onSuccess?.();
      return true;
    } catch (err) {
      setPatchError(err instanceof Error ? err.message : 'Failed to update change request');
      return false;
    } finally {
      setPatching(false);
    }
  }, [api, buildIdParams, onSuccess]);

  return {
    creating,
    createError,
    patching,
    patchError,
    createChangeRequest,
    patchChangeRequest,
    setCreateError,
    setPatchError
  };
};
